<template>
  <el-dialog
    visible
    @close="abortCreate"
  >
    <ContentHeader
      slot="title"
      :title="isModalEdit ?
        $t('samples.update.updateSample') : $t('samples.create.createSample')"
    />
    <ValidationObserver
      ref="observer"
      v-slot="{ handleSubmit, invalid }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          v-if="rename"
          :gutter="20"
        >
          <el-col :span="24">
            <FormItem
              :label="$t('samples.create.productName')"
            >
              <el-input
                v-model="formData.productName"
                :disabled="!editableFields.includes('productName')"
              />
            </FormItem>
          </el-col>
        </el-row>
        <el-row
          v-else
          :gutter="20"
        >
          <el-col :span="8">
            <FormItem
              :label="$t('samples.create.labSampleCode')"
              :help="$t('tooltips.newSample.labSampleCode')"
            >
              <el-input
                v-model="formData.productCode"
                :disabled="!editableFields.includes('productCode')"
              />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('samples.create.productName')"
              :help="$t('tooltips.newSample.productName')"
            >
              <el-input
                v-model="formData.productName"
                :disabled="!editableFields.includes('productName')"
              />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              :label="$t('samples.create.customerProductName')"
              :help="$t('tooltips.newSample.customerSampleCode')"
            >
              <el-input
                v-model="formData.customerProductName"
                :disabled="!editableFields.includes('customerProductName')"
              />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem
              :label="$t('samples.create.lot')"
              :help="$t('tooltips.newSample.lot')"
            >
              <el-input
                v-model="formData.lot"
                :disabled="!editableFields.includes('lot')"
                maxlength="100"
                show-word-limit
                @input="onLotInput"
              />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem
              :label="$t('samples.create.date')"
              :help="$t('tooltips.newSample.lotDate')"
            >
              <el-date-picker
                v-model="formData.date"
                :disabled="!editableFields.includes('date')"
                format="dd-MM-yyyy HH:mm:ss"
                value-format="yyyy-MM-dd HH:mm:ss"
                type="datetime"
              />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem
              :label="$t('samples.create.sector')"
              :help="$t('tooltips.newSample.sector')"
              rules="required"
            >
              <el-select
                v-model="formData.sector"
                :disabled="!editableFields.includes('sector')"
                placeholder=""
                clearable
              >
                <el-option
                  v-for="(option) in sectorOptions"
                  :key="option.value"
                  :label="$t(option.label)"
                  :value="option.value"
                />
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem
              rules="required|numeric"
              :label="$t('samples.create.packagesNumber')"
              :help="$t('tooltips.newSample.lotNumber')"
            >
              <el-input
                v-model.number="formData.packagesNumber"
                :disabled="!editableFields.includes('packagesNumber')"
                type="number"
                :min="1"
                @change="handlePackagesNumberChange"
              />
            </FormItem>
          </el-col>
          <el-col :span="24">
            <FormItem
              :label="$t('samples.create.notes')"
              :help="$t('tooltips.newSample.note')"
            >
              <el-input
                v-model="formData.notes"
                :disabled="!editableFields.includes('notes')"
                type="textarea"
              />
            </FormItem>
          </el-col>
          <el-col
            :span="12"
          >
            <FormItem
              :label="$t('samples.create.finalCustomer')"
              :help="$t('tooltips.newSample.finalCustomer')"
            >
              <el-autocomplete
                v-model="formData.finalCustomer"
                :disabled="!editableFields.includes('finalCustomer')"
                style="width: 100%"
                :fetch-suggestions="isUser ? finalCustomerList : adminFinalCustomersList"
                value-key="label"
                placeholder=""
                @input="handleFinalCustomerInput"
              />
            </FormItem>
          </el-col>
          <el-col :span="12">
            <FormItem
              :label="$t('samples.create.finalCustomerRef')"
              :help="$t('tooltips.newSample.finalCustomerRef')"
            >
              <el-input
                v-model="formData.finalCustomerRef"
                :disabled="!editableFields.includes('finalCustomerRef')"
              />
            </FormItem>
          </el-col>
          <el-col :span="12">
            <FormItem
              rules="required"
              :label="$t('samples.create.sampleDescription')"
            >
              <template #help>
                <SampleDescriptionHelp />
              </template>
              <el-select
                v-model="formData.sampleDescription"
                filterable
                :disabled="!editableFields.includes('sampleDescription')"
                placeholder=""
                clearable
              >
                <el-option
                  v-for="(option) in sampleDescriptionOptions"
                  :key="option.value"
                  :label="$t(option.label)"
                  :value="option.value"
                />
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="12">
            <FormItem
              :label="$t('samples.create.costCenter')"
              :help="$t('tooltips.newSample.costCenter')"
            >
              <el-input
                v-model="formData.costCenter"
                :disabled="!editableFields.includes('costCenter')"
                :maxlength="100"
                show-word-limit
              />
            </FormItem>
          </el-col>
          <el-col
            v-if="formData.finalCustomer"
            :span="8"
          >
            <FormItem
              :label="$t('samples.create.customerHeaderData')"
            >
              <el-switch
                v-model="formData.customerHeaderData"
                :disabled="!editableFields.includes('customerHeaderData')"
                :active-text="$t('samples.create.customerHeaderDataYes')"
                :inactive-text="$t('samples.create.customerHeaderDataNo')"
                @change="resetAddress"
              />
            </FormItem>
          </el-col>
          <el-col
            v-if="formData.customerHeaderData"
            :span="16"
          >
            <FormItem
              :label="$t('samples.create.customerAddress')"
            >
              <el-select
                v-model="formData.addressId"
                :disabled="!editableFields.includes('addressId')"
                filterable
                :filter-method="query => { addressQuery = query; }"
                @visible-change="opened => opened && (addressQuery = '')"
                @change="selectAddress"
              >
                <el-option
                  :value="null"
                  :label="$t('samples.create.address.addNew')"
                />
                <el-option
                  v-for="item in filteredAddresses"
                  :key="item.id"
                  :value="item.id"
                  :label="addressLabel(item)"
                />
              </el-select>
            </FormItem>
          </el-col>
        </el-row>
        <el-row
          v-if="formData.customerHeaderData"
          :gutter="20"
        >
          <el-col :span="8">
            <FormItem
              vid="name"
              :rules="addressFieldRules.name"
              :label="$t('samples.create.address.name')"
            >
              <el-input
                v-model="address.name"
                :maxlength="100"
                show-word-limit
                :disabled="!addressEditable"
              />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              vid="address"
              :label="$t('samples.create.address.address')"
            >
              <el-input
                v-model="address.address"
                :maxlength="100"
                show-word-limit
                :disabled="!addressEditable"
              />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              vid="city"
              :label="$t('samples.create.address.city')"
            >
              <el-input
                v-model="address.city"
                :maxlength="30"
                show-word-limit
                :disabled="!addressEditable"
              />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              vid="zip"
              :label="$t('samples.create.address.zip')"
            >
              <el-input
                v-model="address.zip"
                :maxlength="6"
                :disabled="!addressEditable"
              />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              vid="countryCode"
              :rules="addressFieldRules.countryCode"
              :label="$t('samples.create.address.countryCode')"
            >
              <el-select
                v-model="address.countryCode"
                filterable
                clearable
                placeholder=""
                :disabled="!addressEditable"
                @visible-change="open => open && loadCountries()"
              >
                <el-option
                  v-for="option in countryOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              vid="province"
              :rules="addressFieldRules.province"
              :label="$t('samples.create.address.province')"
            >
              <el-input
                v-model="address.province"
                :maxlength="provinceMaxLength(address.countryCode)"
                :disabled="!addressEditable"
              />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              vid="phoneNumber"
              :label="$t('samples.create.address.phoneNumber')"
            >
              <el-input
                v-model="address.phoneNumber"
                :maxlength="14"
                :disabled="!addressEditable"
              />
            </FormItem>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="24">
            <FormItem
              :label="$t('samples.create.comments')"
              :help="$t('tooltips.newSample.comments')"
            >
              <el-input
                v-model="formData.comments"
                :disabled="!editableFields.includes('comments')"
                type="textarea"
              />
            </FormItem>
          </el-col>

          <el-col :span="24">
            <FormItem
              :label="$t('global.uploadPdf')"
              :help="$t('tooltips.newSample.pdfUpload')"
            >
              <div
                v-for="(attachment, index) in previouslyUploadedFiles"
                :key="index"
                class="attachments-list"
              >
                <span class="attachment-filename">{{ attachment.filename }}</span>
                <div>
                  <el-button
                    type="primary"
                    icon="el-icon-download"
                    circle
                    @click.prevent="downloadAttachedPdf(attachment)"
                  />
                  <el-button
                    type="danger"
                    icon="el-icon-delete"
                    circle
                    @click.prevent="removeAttachedSamplePdf(attachment)"
                  />
                </div>
              </div>
              <el-upload
                ref="uploadComponent"
                action=""
                accept="application/pdf"
                multiple
                :on-change="handleChangeAttachment"
                :on-remove="handleRemoveAttachment"
                :before-remove="beforeRemoveAttachment"
                :auto-upload="false"
              >
                <el-button
                  size="small"
                  type="primary"
                >
                  {{ $t('global.browse') }}
                </el-button>
                <div
                  slot="tip"
                  class="el-upload__tip"
                >
                  {{ $t('samples.create.fileSize') }}
                </div>
              </el-upload>
            </FormItem>
          </el-col>
        </el-row>
        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortCreate"
          >
            {{ $t('samples.create.abortCreate') }}
          </el-button>
          <el-button
            v-if="isModalEdit"
            type="primary"
            native-type="submit"
            :disabled="invalid"
            @click.prevent="handleSubmit(handleUpdate)"
          >
            {{ $t('samples.update.updateSample') }}
          </el-button>
          <el-button
            v-else
            type="primary"
            native-type="submit"
            :disabled="invalid"
            @click.prevent="handleSubmit(createSample)"
          >
            {{ $t('samples.create.createSample') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
import fileSaver from 'file-saver';
import { mapGetters } from 'vuex';

import {
  ordersApi, samplesApi, customersApi, countriesApi,
} from '@/api';
import FormItem from '@/components/FormItem.vue';
import SampleDescriptionHelp from '@/components/SampleDescriptionHelp.vue';
import ContentHeader from '@/components/ContentHeader.vue';
import FormButtons from '@/components/FormButtons.vue';
import { sectorsList, sampleDescriptionsList, SAMPLE_STATUSES } from '@/config/constants';
import { enumOptions } from '@/config/enumOptions';
import countryLabel from '@/services/countryOptions';
import {
  addressRules, provinceMaxLength, newAddressPayload, addressChanges, addressErrors, addressLabel,
  addressMatches,
} from '@/services/address';

const defaultData = {
  productCode: null,
  productName: null,
  customerProductName: null,
  lot: null,
  date: null,
  sector: null,
  packagesNumber: null,
  notes: null,
  finalCustomer: null,
  finalCustomerRef: null,
  customerAddress: null,
  customerHeaderData: false,
  sampleDescription: null,
  comments: null,
  costCenter: null,
  orderId: null,
  addressId: null,
};

const defaultAddressData = {
  name: null,
  address: null,
  city: null,
  phoneNumber: null,
  province: null,
  zip: null,
  countryCode: null,
};

export default {
  name: 'CreateSample',
  components: {
    FormItem,
    SampleDescriptionHelp,
    ContentHeader,
    FormButtons,
  },
  props: {
    rename: { type: Boolean, default: false },
    order: { type: Object, default: () => {} },
    visible: { type: Boolean, default: false },
    isModalEdit: { type: Boolean, default: false },
    initialData: { type: Object, default: () => defaultData },
  },
  data() {
    return {
      formData: { ...defaultData },
      sectorsList,
      sampleDescriptionsList,
      attachedFiles: [],
      previouslyUploadedFiles: [],
      addresses: [],
      addressQuery: '',
      customerSearch: null,
      customerId: null,
      address: { ...defaultAddressData },
      countries: [],
      finalCustomers: {
        searched: false,
        data: [],
      },
    };
  },
  computed: {
    filteredAddresses() {
      return this.addresses.filter(item => addressMatches(item, this.addressQuery));
    },
    sectorOptions() {
      return enumOptions(sectorsList, 'samples.sectors', this.formData.sector);
    },
    sampleDescriptionOptions() {
      return enumOptions(
        sampleDescriptionsList,
        'samples.sampleDescriptions',
        this.formData.sampleDescription,
      );
    },
    countryOptions() {
      return this.countries.map(country => ({
        value: country.code,
        label: countryLabel(country),
      }));
    },
    // Once the sample is past the point of changing its address, the address is read-only.
    addressEditable() {
      return this.editableFields.includes('addressId');
    },
    // A read-only address may predate the Country Code: never block on it.
    addressFieldRules() {
      return this.addressEditable
        ? addressRules(this.address.countryCode)
        : { name: '', countryCode: '', province: '' };
    },
    ...mapGetters([
      'userPermissions',
      'loggedUser',
    ]),
    isCloneMode() {
      return !this.isModalEdit && this.$route.params.sampleId;
    },
    editableFields() {
      if (this.isModalEdit) {
        switch (this.initialData.status) {
          case SAMPLE_STATUSES.CREATED:
            return Object.keys(this.formData);
          case SAMPLE_STATUSES.PENDING:
            return [
              'productCode',
              'productName',
              'customerProductName',
              'lot',
              'packagesNumber',
              'date',
              'notes',
              'finalCustomer',
              'finalCustomerRef',
              'costCenter',
              'sampleDescription',
              'comments',
              'status',
            ];
          case SAMPLE_STATUSES.DRAFT:
            return ['productName'];
          case SAMPLE_STATUSES.ACCEPTED:
          default:
            return [];
        }
      } else {
        return Object.keys(this.formData);
      }
    },
    isUser() {
      return this.userPermissions && this.userPermissions.includes('user');
    },
    isAdmin() {
      return this.userPermissions && this.userPermissions.includes('admin');
    },
  },
  watch: {
    initialData: {
      immediate: true,
      handler(data) {
        if ((this.isModalEdit || this.isCloneMode) && Object.keys(data).length > 0) {
          this.formData = { ...data };
          this.formData.customerHeaderData = !!data.addressId;
        }
      },
    },
    'formData.customerHeaderData': {
      immediate: true,
      handler(customerHeaderData) {
        if (customerHeaderData) this.loadCountries();
      },
    },
  },
  async created() {
    if (
      this.isUser
      && this.loggedUser.customerId) {
      const addresses = await customersApi.getCustomerAddresses(this.loggedUser.customerId);
      this.addresses = addresses.data;
      if (this.isModalEdit && this.formData.addressId) {
        this.selectAddress(this.formData.addressId);
      }
    }
    if (this.isAdmin) {
      this.customerId = this.order.customerId;
      const addresses = await customersApi.getCustomerAddresses(this.customerId);
      this.addresses = addresses.data;
      if (this.isModalEdit) {
        if (this.formData.addressId) {
          this.selectAddress(this.formData.addressId);
        }
      }
    }
    if (this.isModalEdit) {
      const attachments = await samplesApi.getSampleAttachments(this.$route.params.sampleId);
      this.previouslyUploadedFiles = attachments.data || [];
    }
  },
  methods: {
    buildFormToSend() {
      if (this.rename) {
        return { productName: this.formData.productName };
      }
      if (this.isModalEdit) {
        const keys = Object.keys(this.initialData);
        const usedKeys = keys
          .filter(item => this.initialData[item] !== this.formData[item] && item);
        const data = {};
        usedKeys.forEach((key) => {
          data[key] = this.formData[key];
        });
        return data;
      }
      return Object.assign(
        // required fields
        {
          productName: this.formData.productName,
          packagesNumber: this.formData.packagesNumber,
          sampleDescription: this.formData.sampleDescription,
          date: this.formData.date,
          orderId: this.order.id,
        },
        // optional fields
        this.formData.productCode !== null && {
          productCode: this.formData.productCode,
        },
        this.formData.customerProductName !== null && {
          customerProductName: this.formData.customerProductName,
        },
        this.formData.lot !== null && {
          lot: this.formData.lot,
        },
        this.formData.sector !== null && {
          sector: this.formData.sector,
        },
        this.formData.notes !== null && {
          notes: this.formData.notes,
        },
        this.formData.finalCustomer !== null && {
          finalCustomer: this.formData.finalCustomer,
        },
        this.formData.finalCustomerRef !== null && {
          finalCustomerRef: this.formData.finalCustomerRef,
        },
        this.formData.customerAddress !== null && {
          customerAddress: this.formData.customerAddress,
        },
        this.formData.comments !== null && {
          comments: this.formData.comments,
        },
        this.formData.costCenter !== null && {
          costCenter: this.formData.costCenter,
        },
      );
    },
    async createSample() {
      try {
        let addressId = null;
        if (this.formData.customerHeaderData) {
          addressId = await this.saveAddress();
          if (!addressId) return;
        }

        const sampleData = this.buildFormToSend();
        if (addressId) sampleData.addressId = addressId;
        // in clone sample id exist
        const sampleCreatedResponse = await ordersApi.createSample(
          this.order.id,
          sampleData,
        );
        // Uploads file if attached
        if (this.attachedFiles.length) {
          const formData = new FormData();
          this.attachedFiles.forEach(file => formData.append('pdf', file.raw, file.name));
          await samplesApi.uploadSamplePdf(sampleCreatedResponse.data.id, formData);
        }
        this.$notify({ showClose: true, type: 'success', message: this.$t('samples.create.success') });
        this.$emit('dialogClose', { success: true, sampleCreated: sampleCreatedResponse.data });
        this.formData = { ...defaultData };
      } catch (error) {
        this.handleSaveError(error);
      }
    },
    async updateSample() {
      try {
        if (this.formData.customerHeaderData) {
          const addressId = await this.saveAddress();
          if (!addressId) return;
          // FIX for new addressId not being sent in PATCH request
          this.formData.addressId = addressId;
        }
        const sampleData = this.buildFormToSend();
        await ordersApi.patchSample(
          this.$route.params.orderId,
          this.$route.params.sampleId,
          sampleData,
        );
        if (this.attachedFiles.length) {
          const formData = new FormData();
          this.attachedFiles.forEach(file => formData.append('pdf', file.raw, file.name));
          await samplesApi.uploadSamplePdf(this.$route.params.sampleId, formData);
        }
        this.$notify({
          showClose: true,
          type: 'success',
          message: this.$t('samples.update.success'),
        });
        this.$emit('dialogClose', {
          success: true,
          updatedSample: { ...this.formData, id: this.$route.params.sampleId },
        });
        this.formData = { ...defaultData };
      } catch (error) {
        this.handleSaveError(error);
      }
    },
    /**
     * Creates the new address, or saves the edits to the chosen one (every sample using it sees
     * them). Returns the address id, or null once the user has been told why it was not saved.
     */
    async saveAddress() {
      const customerId = this.customerId || this.loggedUser.customerId;
      const { addressId } = this.formData;
      try {
        if (addressId === null) {
          const { data } = await customersApi.createAddress(
            customerId,
            newAddressPayload(this.address),
          );
          return data.id;
        }
        const stored = this.addresses.find(item => item.id === addressId);
        const changes = addressChanges(stored, this.address);
        if (!this.addressEditable || !Object.keys(changes).length) return addressId;
        const { data } = await customersApi.updateAddress(customerId, addressId, changes);
        this.addresses = this.addresses.map(item => (item.id === addressId ? data : item));
        this.selectAddress(addressId);
        return addressId;
      } catch (error) {
        const { fields, messages } = addressErrors(error, this.$i18n);
        if (Object.keys(fields).length) this.$refs.observer.setErrors(fields);
        const h = this.$createElement;
        const message = h('div', messages.map(line => h('p', line)));
        this.$notify({ showClose: true, type: 'error', message });
        return null;
      }
    },
    handleSaveError(error) {
      const body = error.response && error.response.data;
      const message = (body && body.error && body.error.message) || this.$t('global.connectionError');
      this.$notify({ showClose: true, type: 'error', message });
    },
    provinceMaxLength,
    addressLabel,
    async loadCountries() {
      if (this.countries.length) return;
      try {
        const { data } = await countriesApi.index();
        this.countries = data;
      } catch (error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message: this.$t('samples.create.address.countriesLoadError'),
        });
      }
    },
    handleUpdate() {
      if (this.initialData.status === SAMPLE_STATUSES.PENDING) {
        this.openConfirmModal();
      } else {
        this.updateSample();
      }
    },
    openConfirmModal() {
      this.$confirm(this.$t('samples.update.confirm'), this.$t('global.warning'), {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'info',
      })
        .then(async () => {
          this.updateSample();
        })
        .catch((error) => {
          this.$notify({ showClose: true, type: 'error', message: error.response.data.error.message });
        });
    },
    abortCreate() {
      this.formData = { ...defaultData };
      this.$emit('dialogClose', { success: false });
    },
    handleChangeAttachment(file) {
      const filesize = ((file.size / 1024) / 1024).toFixed(4);
      if (filesize < 10) {
        this.attachedFiles.push(file);
      } else {
        const uploadFileList = this.$refs.uploadComponent.uploadFiles;
        uploadFileList.splice(uploadFileList.indexOf(file), 1);
      }
    },
    handleRemoveAttachment(file) {
      this.attachedFiles = this.attachedFiles.filter(e => e !== file);
    },
    beforeRemoveAttachment() {
      return this.$confirm(this.$t('global.confirmUploadCancel'));
    },
    async removeAttachedSamplePdf(attachment) {
      try {
        this.$confirm(this.$t('global.confirmUploadCancel'), this.$t('global.warning'), {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning',
        })
          .then(async () => {
            await samplesApi.removeAttachedSamplePdf(this.$route.params.sampleId, attachment.id);
            this.previouslyUploadedFiles = this.previouslyUploadedFiles
              .filter(e => e !== attachment);
          });
      } catch (error) {
        this.$notify({ showClose: true, type: 'error', message: error.response.data.error.message });
      }
    },
    async downloadAttachedPdf(attachment) {
      try {
        const file = await samplesApi.downloadAttachedSamplePdf(
          this.$route.params.sampleId, attachment.id,
        );
        if (file) {
          return fileSaver.saveAs(file.data, `attachment-${this.$route.params.sampleId}.pdf`);
        }
        return false;
      } catch (error) {
        return error;
      }
    },
    async finalCustomerList(value, cb) {
      if (!this.finalCustomers.searched) {
        const results = await ordersApi.getFinalCustomerList();
        const filteredResults = results.data.filter(f => f !== null).map(item => (
          {
            label: item,
            value: item,
          }
        ));
        this.finalCustomers.data = filteredResults;
        this.finalCustomers.searched = true;
      }
      cb(this.finalCustomers.data);
    },
    async adminFinalCustomersList(value, cb) {
      if (!this.finalCustomers.searched) {
        const results = await customersApi.getFinalCustomers(
          this.customerId,
        );
        const filteredResults = results.data
          .filter(f => f !== null)
          .map(item => ({
            label: item,
            value: item,
          }));
        this.finalCustomers.data = filteredResults;
        this.finalCustomers.searched = true;
      }
      cb(this.finalCustomers.data);
    },
    selectAddress(addressId) {
      if (addressId === null) {
        this.address = { ...defaultAddressData };
      } else {
        const selectedAddress = this.addresses.find(a => a.id === addressId);
        const {
          name,
          address,
          city,
          phoneNumber,
          province,
          zip,
          countryCode,
        } = selectedAddress;
        this.address = {
          name,
          address,
          city,
          phoneNumber,
          province,
          zip,
          countryCode: countryCode || null,
        };
      }
    },
    resetAddress() {
      this.address = { ...defaultAddressData };
      this.formData.addressId = null;
    },
    handleFinalCustomerInput(value) {
      if (!value || value === '') {
        this.formData.customerHeaderData = false;
        this.formData.finalCustomer = null;
        this.resetAddress();
      }
    },
    handlePackagesNumberChange(value) {
      if (value < 1) {
        this.formData.packagesNumber = 1;
      }
    },
    onLotInput(value) {
      if (value.length > 100) {
        this.formData.lot = value.slice(0, 100);
      } else {
        this.formData.lot = value;
      }
    },
  },
};
</script>

<style lang="scss">
.attachments-list {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  justify-content: space-between;

  .attachment-filename {
    max-width: 80%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
